'use client';

import { ErrorBoundary } from 'react-error-boundary';
import { ErrorInfo } from 'react';
import { safeCaptureException } from '@/utils/sentry-utils';

/**
 * Silent Fallback: 返回 null = 无 UI 变化,只记录日志
 * 错误组件会静默失败,非错误部分继续正常渲染
 */
const SilentFallback = () => null;

/**
 * 错误日志回调:记录到控制台并上报到 Sentry
 * 使用安全包装函数，确保 Sentry 服务挂掉不会影响应用
 */
const handleError = (error: Error, errorInfo: ErrorInfo) => {
  // 控制台日志(开发环境查看)
  console.error('🔇 Silent Error Captured:', error);

  // 安全地上报到 Sentry(生产环境监控)
  // 即使 Sentry 服务挂掉，也不会影响应用运行
  safeCaptureException(error, {
    contexts: {
      react: {
        componentStack: errorInfo.componentStack || '',
      },
    },
  });
};

/**
 * 全局静默错误边界组件
 *
 * 特性:
 * - 捕获所有子组件树中的渲染错误
 * - 不显示任何 fallback UI,让页面继续正常交互
 * - 只记录错误日志,不影响用户体验
 * - 类似 Vue 的 config.errorHandler 行为
 *
 * 使用方式:在根布局中包裹应用
 * ```tsx
 * <SilentErrorBoundary>
 *   <App />
 * </SilentErrorBoundary>
 * ```
 */
export default function SilentErrorBoundary({
  children
}: {
  children: React.ReactNode;
}) {
  return (
    <ErrorBoundary
      FallbackComponent={SilentFallback}
      onError={handleError}
      onReset={() => {
        // 可选:重置应用状态
        // 如果需要全页刷新,可以取消注释下面的代码
        // window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
